import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Dumbbell, User, Loader2, ArrowRight, Building2, Mail } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { roleHome } from "@/components/ProtectedRoute";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const DEMO_ENABLED = process.env.REACT_APP_DEMO_LOGIN === "true";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const QUICK = [
  { role: "admin", label: "Yönetici", icon: Shield, desc: "Tüm operasyon yönetimi" },
  { role: "branch_manager", label: "Şube Yöneticisi", icon: Building2, desc: "Kartal şubesi (izole veri)" },
  { role: "trainer", label: "Antrenör", icon: Dumbbell, desc: "Ders & üye takibi" },
  { role: "member", label: "Üye / Sporcu", icon: User, desc: "Kişisel spor deneyimi" },
];

export default function Login() {
  const { login, demoLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [busyRole, setBusyRole] = useState(null);

  // Forgot-password modal
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotErr, setForgotErr] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);

  useEffect(() => { document.title = "Get Up Fit | Giriş Yap"; }, []);

  const validate = () => {
    const e = {};
    if (!email.trim()) e.email = "E-posta adresi gerekli.";
    else if (!EMAIL_RE.test(email.trim())) e.email = "Geçerli bir e-posta adresi girin.";
    if (!password) e.password = "Şifre gerekli.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!validate()) return;
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Hoş geldin, ${u.name}`);
      navigate(roleHome(u.role));
    } catch (err) {
      const status = err.response?.status;
      const msg = formatApiError(err.response?.data?.detail) || "Giriş başarısız. Lütfen tekrar deneyin.";
      if (status === 401) setErrors({ password: "E-posta veya şifre hatalı." });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const quick = async (role) => {
    if (busyRole !== null) return;
    setBusyRole(role);
    try {
      const u = await demoLogin(role);
      toast.success(`Demo giriş: ${u.name}`);
      navigate(roleHome(u.role));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Demo giriş başarısız.");
    } finally {
      setBusyRole(null);
    }
  };

  const openForgot = () => { setForgotEmail(email.trim()); setForgotErr(""); setForgotOpen(true); };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (forgotBusy) return;
    const val = forgotEmail.trim();
    if (!val) { setForgotErr("E-posta adresi gerekli."); return; }
    if (!EMAIL_RE.test(val)) { setForgotErr("Geçerli bir e-posta adresi girin."); return; }
    setForgotErr("");
    setForgotBusy(true);
    try {
      const r = await api.post("/auth/forgot-password", { email: val });
      if (r.data?.reset_token) {
        toast.success("Sıfırlama bağlantısı hazır.");
        setForgotOpen(false);
        navigate(`/set-password?token=${r.data.reset_token}`);
      } else {
        toast.success("Eğer bu e-posta kayıtlıysa, sıfırlama bağlantısı gönderildi.");
        setForgotOpen(false);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "İşlem başarısız. Lütfen tekrar deneyin.");
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0E] flex flex-col lg:flex-row">
      {/* Brand panel */}
      <div className="relative lg:w-1/2 min-h-[260px] lg:min-h-screen overflow-hidden flex flex-col justify-between lg:justify-end p-8 lg:p-14">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1637430308606-86576d8fef3c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0E] via-[#0B0B0E]/70 to-[#0B0B0E]/30" />
        <div className="relative z-10 self-start lg:absolute lg:top-14 lg:left-14"><Logo /></div>
        <div className="relative z-10 max-w-md mt-8 lg:mt-0">
          <h1 className="font-display font-black text-3xl lg:text-5xl text-white leading-tight tracking-tight">
            Bugün <span className="text-[#FF5500]">başla</span>, hedefini aş.
          </h1>
          <p className="text-sm lg:text-base text-[#A0A0B2] mt-4">
            Get Up Fit üye paneli — randevularını planla, antrenman programını takip et, ilerlemeni gör. Hepsi tek yerde.
          </p>
        </div>
      </div>

      {/* Auth panel */}
      <div className="lg:w-1/2 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <h2 className="font-display font-extrabold text-2xl text-white">Giriş Yap</h2>
          <p className="text-sm text-[#A0A0B2] mt-1 mb-7">Hesabınla panele eriş.</p>

          <form onSubmit={submit} noValidate className="space-y-4">
            <div>
              <label htmlFor="login-email" className="text-xs font-medium text-[#A0A0B2] mb-1.5 block">E-posta</label>
              <Input
                id="login-email" name="email" type="email" autoComplete="email" required
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors((p) => ({ ...p, email: undefined })); }}
                data-testid="login-email-input" placeholder="ornek@getupfit.com"
                aria-invalid={!!errors.email} aria-describedby={errors.email ? "login-email-err" : undefined}
                className="bg-[#0E0E14] border-[#262636] h-11 text-white focus-visible:ring-[#FF5500] focus-visible:border-[#FF5500]" />
              {errors.email && <p id="login-email-err" data-testid="login-email-error" className="text-xs text-red-400 mt-1.5">{errors.email}</p>}
            </div>
            <div>
              <label htmlFor="login-password" className="text-xs font-medium text-[#A0A0B2] mb-1.5 block">Şifre</label>
              <Input
                id="login-password" name="password" type="password" autoComplete="current-password" required
                value={password}
                onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors((p) => ({ ...p, password: undefined })); }}
                data-testid="login-password-input" placeholder="••••••••"
                aria-invalid={!!errors.password} aria-describedby={errors.password ? "login-password-err" : undefined}
                className="bg-[#0E0E14] border-[#262636] h-11 text-white focus-visible:ring-[#FF5500] focus-visible:border-[#FF5500]" />
              {errors.password && <p id="login-password-err" data-testid="login-password-error" className="text-xs text-red-400 mt-1.5">{errors.password}</p>}
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full h-11 bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold rounded-xl shadow-[0_4px_20px_rgba(255,85,0,0.3)]">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <>Giriş Yap <ArrowRight size={18} className="ml-1" /></>}
            </Button>
            <button type="button" onClick={openForgot} data-testid="forgot-password-link" className="w-full text-center text-xs text-[#68687B] hover:text-[#FF5500] mt-1">Şifremi unuttum?</button>
            <a href="/kayit" data-testid="go-register-link" className="block w-full text-center text-sm text-[#FF5500] hover:underline mt-3">Hesabın yok mu? Yeni Üye Ol</a>
          </form>

          {DEMO_ENABLED && (
            <div className="mt-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px flex-1 bg-[#222230]" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#68687B]">Hızlı Demo Girişi</span>
                <div className="h-px flex-1 bg-[#222230]" />
              </div>
              <div className="space-y-2.5">
                {QUICK.map((q) => {
                  const Icon = q.icon;
                  return (
                    <button key={q.role} type="button" onClick={() => quick(q.role)} disabled={busyRole !== null}
                      data-testid={`quick-login-${q.role}-button`}
                      className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#13131C] border border-[#222230] hover:border-[#FF5500]/50 hover:bg-[#1B1B27] transition-colors text-left disabled:opacity-50">
                      <div className="w-10 h-10 rounded-lg bg-[#FF5500]/10 flex items-center justify-center">
                        {busyRole === q.role ? <Loader2 size={18} className="text-[#FF5500] animate-spin" /> : <Icon size={18} className="text-[#FF5500]" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-white">{q.label} olarak gir</p>
                        <p className="text-xs text-[#68687B]">{q.desc}</p>
                      </div>
                      <ArrowRight size={16} className="text-[#68687B]" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="bg-[#13131C] border-[#222230] text-white sm:max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail size={18} className="text-[#FF5500]" /> Şifre Sıfırlama</DialogTitle>
            <DialogDescription className="text-[#A0A0B2]">
              Hesabına ait e-posta adresini gir. Kayıtlıysa sıfırlama bağlantısı gönderilecek.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitForgot} noValidate className="space-y-3">
            <div>
              <label htmlFor="forgot-email" className="text-xs font-medium text-[#A0A0B2] mb-1.5 block">E-posta</label>
              <Input
                id="forgot-email" name="email" type="email" autoComplete="email"
                value={forgotEmail}
                onChange={(e) => { setForgotEmail(e.target.value); if (forgotErr) setForgotErr(""); }}
                data-testid="forgot-email-input" placeholder="ornek@getupfit.com"
                aria-invalid={!!forgotErr} aria-describedby={forgotErr ? "forgot-email-err" : undefined}
                className="bg-[#0E0E14] border-[#262636] h-11 text-white focus-visible:ring-[#FF5500] focus-visible:border-[#FF5500]" />
              {forgotErr && <p id="forgot-email-err" data-testid="forgot-email-error" className="text-xs text-red-400 mt-1.5">{forgotErr}</p>}
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="ghost" onClick={() => setForgotOpen(false)} data-testid="forgot-cancel-button" className="text-[#A0A0B2] hover:text-white">İptal</Button>
              <Button type="submit" disabled={forgotBusy} data-testid="forgot-submit-button" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold">
                {forgotBusy ? <Loader2 className="animate-spin" size={16} /> : "Bağlantı Gönder"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
